import React, { useMemo, useState } from 'react';
import {
  Users,
  ChevronDown,
  User,
  ShieldCheck,
  Cpu,
  Network,
  Briefcase,
} from 'lucide-react';

interface Person {
  id: number;
  name: string;
  role: string;
  status: 'active' | 'away';
}

interface PersonWithInTime extends Person {
  inTime: string;
}

const personnel: Person[] = [
  { id: 1, name: "Pavan Kalyan", role: "Lead Software Engineer", status: 'active' },
  { id: 2, name: "Yohan", role: "Senior Software Engineer", status: 'active' },
  { id: 3, name: "Likith", role: "Network Engineer", status: 'active' },
  { id: 4, name: "Dhanush", role: "Software Engineer", status: 'away' },
  { id: 5, name: "Venkatesh", role: "QA Engineer", status: 'active' },
  { id: 6, name: "Malik", role: "Software Engineer", status: 'active' },
  { id: 7, name: "Khaja", role: "Lead IoT Engineer", status: 'active' }
];

const RoleIcon: React.FC<{ role: string }> = ({ role }) => {
  if (role.includes('Lead') || role.includes('Senior')) {
    return <ShieldCheck className="w-3 h-3 text-orange-500" />;
  }
  if (role.includes('Network')) {
    return <Network className="w-3 h-3 text-blue-500" />;
  }
  if (role.includes('QA')) {
    return <Cpu className="w-3 h-3 text-emerald-500" />;
  }
  return <Briefcase className="w-3 h-3 text-purple-500" />;
};

const getRandomInTimeForToday = () => {
  const today = new Date();
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    8,
    0,
    0,
    0
  );
  const end = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    9,
    30,
    0,
    0
  );

  const randomTime =
    start.getTime() + Math.random() * (end.getTime() - start.getTime());

  return new Date(randomTime).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
};

export default function PersonnelOnSite() {
  const [isOpen, setIsOpen] = useState(false);

  const personnelWithInTime: PersonWithInTime[] = useMemo(() => {
    return personnel.map((person) => ({
      ...person,
      inTime: getRandomInTimeForToday(),
    }));
  }, []);

  const activeCount = personnelWithInTime.length;

  return (
    <div className="flex flex-col gap-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all duration-300 group
          ${
            isOpen
              ? 'bg-gray-900/90 border-orange-500/50 shadow-[0_0_15px_rgba(249,115,22,0.15)]'
              : 'bg-black/40 backdrop-blur-md border-white/5 hover:border-white/20 hover:bg-black/60 shadow-lg'
          }`}
      >
        <div
          className={`p-1 rounded-md transition-colors duration-300 ${
            isOpen
              ? 'bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.4)]'
              : 'bg-gray-800'
          }`}
        >
          <Users className={`w-3.5 h-3.5 ${isOpen ? 'text-white' : 'text-orange-500'}`} />
        </div>

        <div className="flex flex-col items-start min-w-[110px]">
          <span className="text-[8px] font-black uppercase tracking-[0.15em] text-gray-400 group-hover:text-gray-300 transition-colors">
            Staff on Site
          </span>
          <span className="text-xs font-black text-white leading-tight flex items-center gap-1.5">
            {activeCount} Active
            <span className="flex h-1 w-1 rounded-full bg-emerald-500 animate-pulse" />
          </span>
        </div>

        <div className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown className="w-3 h-3 text-gray-500" />
        </div>
      </button>

      {isOpen && (
        <div className="w-[240px] bg-gray-900/95 backdrop-blur-xl border border-white/5 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 z-50">
          <div className="p-2.5 border-b border-white/5 bg-gradient-to-br from-orange-500/10 to-transparent">
            <h4 className="text-[9px] font-black text-white uppercase tracking-widest flex items-center gap-2">
              Active In Facility
            </h4>
          </div>

          <div className="max-h-[300px] overflow-y-auto custom-scroll p-1.5">
            {personnelWithInTime.map((person) => (
              <div
                key={person.id}
                className="flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-white/5 transition-all group cursor-default"
              >
                <div className="relative">
                  <div className="w-7 h-7 rounded-full bg-gray-800 border border-white/5 flex items-center justify-center group-hover:border-orange-500/30 transition-colors overflow-hidden">
                    <User className="w-3.5 h-3.5 text-gray-500 group-hover:text-orange-500 transition-colors" />
                  </div>
                  <span
                    className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-gray-900 ${
                      person.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'
                    }`}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-bold text-gray-100 truncate group-hover:text-white transition-colors">
                      {person.name}
                    </p>
                    <span className="shrink-0 text-[8px] font-semibold uppercase tracking-wide text-orange-400">
                      In {person.inTime}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <RoleIcon role={person.role} />
                    <p className="text-[8px] font-medium text-gray-500 uppercase tracking-tight truncate">
                      {person.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}